import { z } from 'zod';
import { UuidSchema } from './common.js';

/** Route param: the course id being shortlisted/unshortlisted. */
export const ShortlistCourseParamSchema = z.object({ courseId: UuidSchema });

/** Max courses comparable at once — keeps the side-by-side table bounded. */
export const MAX_COMPARE_IDS = 6;

/**
 * Query for GET /compare — Brief API-2. `ids` was previously plucked raw off
 * `req.query`; the shape is validated here so the controller's boundary call is
 * a single `Schema.parse(req.query)` like every other controller. Both wire
 * forms the app can produce are accepted: one comma-separated value (what the
 * web sends) or a repeated param. The ids themselves are validated by
 * `parseCompareIds`.
 */
export const CompareQuerySchema = z.object({
  ids: z.union([z.string(), z.array(z.string())]),
});

const CompareIdsSchema = z.array(UuidSchema).min(1).max(MAX_COMPARE_IDS);

/**
 * Parse the compare `ids` query param: a comma-separated list of course UUIDs.
 * De-duplicates while preserving order, caps the count, and validates each id.
 * Throws (Zod) on a malformed id.
 */
export function parseCompareIds(raw: unknown): string[] {
  const str = typeof raw === 'string' ? raw : Array.isArray(raw) ? raw.join(',') : '';
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const part of str.split(',').map((s) => s.trim())) {
    if (part && !seen.has(part)) {
      seen.add(part);
      ids.push(part);
    }
  }
  return CompareIdsSchema.parse(ids);
}
